import { FormContainer, LabeledInput, Worksheet, formatCurrency } from "uikit"
import React from "react"
import { Checkbox, Grid, Dropdown } from "semantic-ui-react"
import { useStore } from "store"
import { Footer } from "./Footer"

const CreditCardCheckboxGroup = ({ creditCardAccounts, accountIds }) => (
  <Grid columns="equal" style={{ padding: "1.5em 0 1.5em 0" }}>
    {Object.keys(creditCardAccounts).map(accountId => (
      <Grid.Row textAlign="left" key={accountId} style={{ padding: "0px" }}>
        <Grid.Column>
          <Checkbox
            label={`${creditCardAccounts[accountId].name} (${formatCurrency(
              -creditCardAccounts[accountId].balance
            )})`}
            checked={
              !!accountIds.array.find(
                element => element.fields.id.value === accountId
              )
            }
            onChange={(event, eventData) => {
              if (eventData.checked) {
                accountIds.append({ id: accountId })
              } else {
                accountIds.array
                  .find(element => element.fields.id.value === accountId)
                  .remove()
              }
            }}
          />
        </Grid.Column>
      </Grid.Row>
    ))}
  </Grid>
)

const DebtPayoffForm = ({
  data: {
    fields: { method, monthly_amount, account_ids, account_id_error },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  submitLabel = "Done",
  submitHeader = "",
  onDelete,
  baseUrl,
  creditCardAccounts,
  isNew
}) => (
  <Worksheet error={error}>
    <Worksheet.Body>
      <Worksheet.Row label="What credit cards do you want to payoff?">
        <Worksheet.Field padded error={account_id_error.error}>
          <CreditCardCheckboxGroup
            creditCardAccounts={creditCardAccounts}
            accountIds={account_ids}
          />
        </Worksheet.Field>
      </Worksheet.Row>
      {method.value !== "optimized" && (
        <Worksheet.Row
          label="What accelerated payoff method would you like to use?"
          description={
            <>
              You created this goal with an earlier version of our planing
              engine. We recommend using our optimized debt-payoff method that
              dynamically optimizes paying off your debt early while balancing
              debt pay-off with your other goals.
            </>
          }
        >
          <Worksheet.Field error={method.error}>
            <Dropdown
              {...method}
              fluid
              selection
              placeholder="Payoff Method"
              options={[
                {
                  value: "proportional",
                  text: "Proportional"
                },
                {
                  value: "optimized",
                  text: "Optimized"
                }
              ]}
            />
          </Worksheet.Field>
        </Worksheet.Row>
      )}
      {method.value !== "optimized" && (
        <Worksheet.Row label="How much are you able to put towards this goal each month?">
          <Worksheet.Field error={monthly_amount.error}>
            <LabeledInput
              {...monthly_amount}
              label=""
              iconText="$"
              iconPosition="left"
              placeholder="0"
            />
          </Worksheet.Field>
        </Worksheet.Row>
      )}
    </Worksheet.Body>

    <Footer
      baseUrl={baseUrl}
      isNew={isNew}
      submitLabel={submitLabel}
      onSubmit={onSubmit}
      isPending={isPending}
      onDelete={onDelete}
      isModified={isModified}
      deleteLabel="Delete Goal"
      error={error}
    />
  </Worksheet>
)

const DebtPayoffFormContainer = FormContainer(
  {
    account_ids: {
      type: "array",
      schema: {
        id: {
          type: "string"
        }
      }
    },
    method: {
      type: "string",
      defaultValue: "optimized"
    },
    monthly_amount: {
      type: "unit",
      minValue: 10,
      required: false
    },
    account_id_error: {
      type: "string",
      required: false,
      submit: false
    },
    type: {
      type: "string",
      defaultValue: "debtpayoff"
    }
  },
  ({ fields: { account_ids, account_id_error } }, ...form) => {
    if (account_ids.array.length === 0) {
      return {
        fields: {
          account_id_error: `You should select at least one account.`
        }
      }
    }
  }
)(DebtPayoffForm)

const DebtPayoff = ({ household, goal, scenario, ...formProps }) => {
  const [accounts, { addGoal, deleteGoal, updateGoal }] = useStore(
    "householdAccounts",
    household.id
  )
  const isNew = goal === null
  const allCreditCardAccounts = Object.values(accounts).filter(
    ({ type }) => type === "creditcard"
  )

  let values = {}

  const creditCardAccounts = allCreditCardAccounts
    .filter(({ is_noncarried }) => is_noncarried === false)
    .reduce(
      (account, current) => Object.assign(account, { [current.id]: current }),
      {}
    )

  if (!isNew) {
    // const result = await household.getGoal(goalId)
    const carriedAccountIds = goal.account_ids.filter(accountId => {
      return creditCardAccounts[accountId]
    })
    values = Object.assign(
      {},
      {
        account_ids: carriedAccountIds.map(accountId => {
          return { id: accountId }
        }),
        monthly_amount: goal.monthly_amount,
        method: goal.method
      }
    )
  }

  const submit = isNew
    ? async values => {
        Object.assign(values, {
          account_ids: values.account_ids.map(account => {
            return account.id
          })
        })
        if (values.method === "optimized") {
          values.monthly_amount = null
        }
        await addGoal({
          scenario_id: scenario.id,
          household_id: household.id,
          ...values
        })
      }
    : async values => {
        Object.assign(values, {
          account_ids: values.account_ids.map(account => {
            return account.id
          })
        })
        if (values.method === "optimized") {
          values.monthly_amount = null
        }
        await updateGoal({ id: goal.id, ...values })
      }

  const onDelete = async () => {
    await deleteGoal({ scenario_id: scenario.id, id: goal.id })
    return true
  }
  return (
    <DebtPayoffFormContainer
      {...{
        submit,
        onDelete,
        values,
        creditCardAccounts,
        isNew,
        ...formProps
      }}
    />
  )
}

export default DebtPayoff
